import { defineStore } from 'pinia'
import { ref } from 'vue'
import client from '../api/client.js'

export const useBillingStore = defineStore('billing', () => {
  const rates = ref([])
  const loading = ref(false)

  async function fetchRates() {
    loading.value = true
    try {
      const { data } = await client.get('/internal/v1/billing/rates')
      rates.value = data
      return data
    } catch (e) { throw e } finally { loading.value = false }
  }

  async function createRate(data) {
    loading.value = true
    try {
      const { data } = await client.post('/internal/v1/billing/rates', data)
      rates.value.unshift(data)
      return data
    } catch (e) { throw e } finally { loading.value = false }
  }

  async function updateRate(rateId, data) {
    loading.value = true
    try {
      const { data } = await client.put(`/internal/v1/billing/rates/${rateId}`, data)
      return data
    } catch (e) { throw e } finally { loading.value = false }
  }

  async function deleteRate(rateId) {
    loading.value = true
    try {
      await client.delete(`/internal/v1/billing/rates/${rateId}`)
    } catch (e) { throw e } finally { loading.value = false }
  }

  async function updateRateRevenueShare(rateId, data) {
    loading.value = true
    try {
      const { data } = await client.put(`/internal/v1/billing/rates/${rateId}/revenue-share`, data)
      return data
    } catch (e) { throw e } finally { loading.value = false }
  }

  async function updateRateDescription(rateId, data) {
    loading.value = true
    try {
      const { data } = await client.put(`/internal/v1/billing/rates/${rateId}/description`, data)
      return data
    } catch (e) { throw e } finally { loading.value = false }
  }

  async function updateRateEffectiveDates(rateId, data) {
    loading.value = true
    try {
      const { data } = await client.put(`/internal/v1/billing/rates/${rateId}/effective-dates`, data)
      return data
    } catch (e) { throw e } finally { loading.value = false }
  }

  async function updateRatePriceTier(rateId, data) {
    loading.value = true
    try {
      const { data } = await client.put(`/internal/v1/billing/rates/${rateId}/price-tier`, data)
      return data
    } catch (e) { throw e } finally { loading.value = false }
  }

  async function updateRateProductType(rateId, data) {
    loading.value = true
    try {
      const { data } = await client.put(`/internal/v1/billing/rates/${rateId}/product-type`, data)
      return data
    } catch (e) { throw e } finally { loading.value = false }
  }

  // ─── Billing Events ────────────────────────────────────────────────

  async function createBillingEvent(data) {
    loading.value = true
    try {
      const { data } = await client.post('/internal/v1/billing/events', data)
      return data
    } catch (e) { throw e } finally { loading.value = false }
  }

  // ─── Refunds ────────────────────────────────────────────────────────

  async function refundTransaction(txId, data) {
    loading.value = true
    try {
      const { data } = await client.post(`/internal/v1/billing/refunds/transactions/${txId}`, data)
      return data
    } catch (e) { throw e } finally { loading.value = false }
  }

  async function refundCampaign(campaignId, data) {
    loading.value = true
    try {
      const { data } = await client.post(`/internal/v1/billing/refunds/campaigns/${campaignId}`, data)
      return data
    } catch (e) { throw e } finally { loading.value = false }
  }

  // ─── Invoices ───────────────────────────────────────────────────────

  async function createInvoice(data) {
    loading.value = true
    try {
      const { data } = await client.post('/api/v1/billing/invoices', data)
      return data
    } catch (e) { throw e } finally { loading.value = false }
  }

  async function fetchInvoices() {
    loading.value = true
    try {
      const { data } = await client.get('/api/v1/billing/invoices')
      return data
    } catch (e) { throw e } finally { loading.value = false }
  }

  async function payInvoice(invoiceNumber, data) {
    loading.value = true
    try {
      const { data } = await client.post(`/api/v1/billing/invoices/${invoiceNumber}/pay`, data)
      return data
    } catch (e) { throw e } finally { loading.value = false }
  }

  // ─── Wallets ────────────────────────────────────────────────────────

  async function createWallet(data) {
    loading.value = true
    try {
      const { data } = await client.post('/api/v1/billing/wallets', data)
      return data
    } catch (e) { throw e } finally { loading.value = false }
  }

  async function getWallet(advertiserId) {
    loading.value = true
    try {
      const { data } = await client.get(`/api/v1/billing/wallets/${advertiserId}`)
      return data
    } catch (e) { throw e } finally { loading.value = false }
  }

  async function topupWallet(advertiserId, data) {
    loading.value = true
    try {
      const { data } = await client.post(`/api/v1/billing/wallets/${advertiserId}/topup`, data)
      return data
    } catch (e) { throw e } finally { loading.value = false }
  }

  async function getWalletTransactions(advertiserId) {
    loading.value = true
    try {
      const { data } = await client.get(`/api/v1/billing/wallets/${advertiserId}/transactions`)
      return data
    } catch (e) { throw e } finally { loading.value = false }
  }

  async function getWalletSummary(advertiserId) {
    loading.value = true
    try {
      const { data } = await client.get(`/api/v1/billing/wallets/${advertiserId}/summary`)
      return data
    } catch (e) { throw e } finally { loading.value = false }
  }

  return {
    rates,
    loading,
    fetchRates,
    createRate,
    updateRate,
    deleteRate,
    updateRateRevenueShare,
    updateRateDescription,
    updateRateEffectiveDates,
    updateRatePriceTier,
    updateRateProductType,
    createBillingEvent,
    refundTransaction,
    refundCampaign,
    createInvoice,
    fetchInvoices,
    payInvoice,
    createWallet,
    getWallet,
    topupWallet,
    getWalletTransactions,
    getWalletSummary,
  }
})